import { useQuery } from "@tanstack/react-query";
import { type IdentityApiSdk, type User } from "@packsize/identity-api-sdk";
import { useAuthentication, useIdentityClient } from "@packsize/authentication";
import { useFeatureFlags } from "@packsize/feature-flags";

import { baseRoutes } from "@/api/base-routes";
import { packNetAxios } from "@/lib/api-client";

export const USERS_QUERY_KEY = "users" as const;
export const COMPANY_USERS_QUERY_KEY = "company-users" as const;

const fetchUsers = async (client: IdentityApiSdk): Promise<User[]> => {
	const response = await client.getUsers();

	if (!response.success) {
		return [];
	}

	const users = response.responseData ?? [];

	// Sort users by userName, falling back to lastName
	return users.sort((a, b) => {
		const nameA = a.userName ?? a.lastName ?? "";
		const nameB = b.userName ?? b.lastName ?? "";
		return nameA.localeCompare(nameB);
	});
};

const fetchCompanyUsers = async (): Promise<User[]> => {
	const response = await packNetAxios.get<User[]>(`${baseRoutes.identity}/v2/tenants/users`);

	if (response.status !== 200) {
		return [];
	}

	const users = response.data ?? [];

	// Sort users by userName, falling back to lastName
	return users.sort((a, b) => {
		const nameA = a.userName ?? a.lastName ?? "";
		const nameB = b.userName ?? b.lastName ?? "";
		return nameA.localeCompare(nameB);
	});
};

export const useUsers = () => {
	const { client } = useIdentityClient();
	const { authenticated } = useAuthentication();
	const { isFeatureFlagEnabled } = useFeatureFlags();
	const queryFn = isFeatureFlagEnabled("vaporware-company-link") ? () => fetchCompanyUsers() : () => fetchUsers(client);
	const queryKey = isFeatureFlagEnabled("vaporware-company-link") ? COMPANY_USERS_QUERY_KEY : USERS_QUERY_KEY;

	return useQuery({
		queryFn,
		queryKey: [queryKey],
		enabled: authenticated === true,
	});
};
